<template>
  <div class="user-profile">
    <div class="profile-header">
      <div class="header-content">
        <div class="user-avatar-section">
          <div class="avatar-container">
            <img :src="userInfo.avatar || defaultAvatar" :alt="userInfo.username" />
            <div class="avatar-upload" @click="uploadAvatar">
              <i class="fa fa-camera"></i>
            </div>
          </div>
          <div class="user-basic-info">
            <h2 class="username">{{ userInfo.username }}</h2>
            <div class="user-role">{{ getRoleText(userInfo.role) }}</div>
            <div class="join-date">加入时间：{{ formatDate(userInfo.joinDate) }}</div>
          </div>
        </div>
        <div class="profile-stats">
          <div class="stat-item">
            <div class="stat-number">{{ profileStats.loginDays }}</div>
            <div class="stat-label">登录天数</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">{{ profileStats.studyHours }}</div>
            <div class="stat-label">学习时长(小时)</div>
          </div>
          <div class="stat-item" v-if="userInfo.role === 'student'">
            <div class="stat-number">{{ profileStats.errorCount }}</div>
            <div class="stat-label">错题数量</div>
          </div>
          <div class="stat-item" v-if="userInfo.role === 'teacher'">
            <div class="stat-number">{{ profileStats.studentCount }}</div>
            <div class="stat-label">学生数量</div>
          </div>
        </div>
      </div>
    </div>

    <div class="profile-content">
      <div class="content-tabs">
        <div 
          class="tab-item" 
          :class="{ active: activeTab === 'info' }"
          @click="activeTab = 'info'"
        >
          <i class="fa fa-user"></i>
          基本信息
        </div>
        <div 
          class="tab-item" 
          :class="{ active: activeTab === 'security' }"
          @click="activeTab = 'security'"
        >
          <i class="fa fa-shield-alt"></i>
          安全设置
        </div>
        <div 
          class="tab-item" 
          :class="{ active: activeTab === 'preferences' }"
          @click="activeTab = 'preferences'"
        >
          <i class="fa fa-cog"></i>
          偏好设置
        </div>
      </div>

      <div class="tab-content">
        <!-- 基本信息 -->
        <div v-show="activeTab === 'info'" class="tab-panel">
          <div class="info-section">
            <h3>个人信息</h3>
            <el-form :model="editForm" :rules="formRules" ref="editForm" label-width="100px">
              <el-form-item label="用户名" prop="username">
                <el-input v-model="editForm.username" :disabled="!editMode"></el-input>
              </el-form-item>
              
              <el-form-item label="真实姓名" prop="realName">
                <el-input v-model="editForm.realName" :disabled="!editMode"></el-input>
              </el-form-item>
              
              <el-form-item label="邮箱" prop="email">
                <el-input v-model="editForm.email" :disabled="!editMode"></el-input>
              </el-form-item>
              
              <el-form-item label="手机号" prop="phone">
                <el-input v-model="editForm.phone" :disabled="!editMode"></el-input>
              </el-form-item>
              
              <el-form-item label="性别">
                <el-radio-group v-model="editForm.gender" :disabled="!editMode">
                  <el-radio label="male">男</el-radio>
                  <el-radio label="female">女</el-radio>
                </el-radio-group>
              </el-form-item>
              
              <el-form-item label="生日">
                <el-date-picker
                  v-model="editForm.birthday"
                  type="date"
                  placeholder="选择日期"
                  :disabled="!editMode"
                ></el-date-picker>
              </el-form-item>
              
              <el-form-item label="个人简介">
                <el-input 
                  v-model="editForm.bio" 
                  type="textarea" 
                  :rows="4"
                  :disabled="!editMode"
                  placeholder="介绍一下自己吧..."
                ></el-input>
              </el-form-item>
              
              <el-form-item>
                <el-button v-if="!editMode" type="primary" @click="enableEdit">编辑信息</el-button>
                <template v-else>
                  <el-button type="primary" @click="saveProfile" :loading="saving">保存</el-button>
                  <el-button @click="cancelEdit">取消</el-button>
                </template>
              </el-form-item>
            </el-form>
          </div>
        </div>

        <!-- 安全设置 -->
        <div v-show="activeTab === 'security'" class="tab-panel">
          <div class="security-section">
            <h3>密码设置</h3>
            <el-form :model="passwordForm" :rules="passwordRules" ref="passwordForm" label-width="120px">
              <el-form-item label="当前密码" prop="currentPassword">
                <el-input v-model="passwordForm.currentPassword" type="password" show-password></el-input>
              </el-form-item>
              
              <el-form-item label="新密码" prop="newPassword">
                <el-input v-model="passwordForm.newPassword" type="password" show-password></el-input>
              </el-form-item>
              
              <el-form-item label="确认新密码" prop="confirmPassword">
                <el-input v-model="passwordForm.confirmPassword" type="password" show-password></el-input>
              </el-form-item>
              
              <el-form-item>
                <el-button type="primary" @click="changePassword" :loading="changingPassword">修改密码</el-button>
              </el-form-item>
            </el-form>
          </div>
          
          <div class="security-section">
            <h3>登录记录</h3>
            <div class="login-records">
              <div v-for="record in loginRecords" :key="record.id" class="record-item">
                <div class="record-info">
                  <div class="record-time">{{ formatDateTime(record.loginTime) }}</div>
                  <div class="record-location">{{ record.location }}</div>
                  <div class="record-device">{{ record.device }}</div>
                </div>
                <div class="record-status" :class="record.status">
                  {{ record.status === 'success' ? '成功' : '失败' }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 偏好设置 -->
        <div v-show="activeTab === 'preferences'" class="tab-panel">
          <div class="preferences-section">
            <h3>界面设置</h3>
            <div class="preference-item">
              <div class="preference-label">主题模式</div>
              <el-radio-group v-model="preferences.theme" @change="updatePreferences">
                <el-radio label="light">浅色模式</el-radio>
                <el-radio label="dark">深色模式</el-radio>
                <el-radio label="auto">跟随系统</el-radio>
              </el-radio-group>
            </div>
            
            <div class="preference-item">
              <div class="preference-label">语言设置</div>
              <el-select v-model="preferences.language" @change="updatePreferences">
                <el-option label="简体中文" value="zh-CN"></el-option>
                <el-option label="English" value="en-US"></el-option>
              </el-select>
            </div>
          </div>
          
          <div class="preferences-section">
            <h3>通知设置</h3>
            <div class="preference-item">
              <div class="preference-label">邮件通知</div>
              <el-switch v-model="preferences.emailNotification" @change="updatePreferences"></el-switch>
            </div>
            
            <div class="preference-item">
              <div class="preference-label">桌面通知</div>
              <el-switch v-model="preferences.desktopNotification" @change="updatePreferences"></el-switch>
            </div>
            
            <div class="preference-item">
              <div class="preference-label">学习提醒</div>
              <el-switch v-model="preferences.studyReminder" @change="updatePreferences"></el-switch>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 头像上传对话框 -->
    <el-dialog
      title="上传头像"
      :visible.sync="avatarDialogVisible"
      width="400px"
      center
    >
      <div class="avatar-upload-dialog">
        <el-upload
          class="avatar-uploader"
          action="/api/upload/avatar"
          :show-file-list="false"
          :on-success="handleAvatarSuccess"
          :before-upload="beforeAvatarUpload"
        >
          <img v-if="tempAvatar" :src="tempAvatar" class="avatar-preview">
          <i v-else class="el-icon-plus avatar-uploader-icon"></i>
        </el-upload>
        <div class="upload-tips">
          <p>支持 JPG、PNG 格式，文件大小不超过 2MB</p>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="avatarDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmAvatar" :disabled="!tempAvatar">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { axios } from "../../config/axios";

export default {
  name: 'UserProfile',
  data() {
    return {
      activeTab: 'info',
      editMode: false,
      saving: false,
      changingPassword: false,
      avatarDialogVisible: false,
      tempAvatar: '',
      defaultAvatar: '/img/def_img.png',
      
      // 用户信息
      userInfo: {
        username: '',
        realName: '',
        email: '',
        phone: '',
        gender: '',
        birthday: null,
        bio: '',
        role: '',
        avatar: '',
        joinDate: new Date()
      },
      
      // 编辑表单
      editForm: {},
      
      // 表单验证规则
      formRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' }
        ],
        email: [
          { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
        ],
        phone: [
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ]
      },
      
      // 密码修改表单
      passwordForm: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      
      // 密码验证规则
      passwordRules: {
        currentPassword: [
          { required: true, message: '请输入当前密码', trigger: 'blur' }
        ],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请确认新密码', trigger: 'blur' },
          { validator: this.validateConfirmPassword, trigger: 'blur' }
        ]
      },
      
      // 统计数据
      profileStats: {
        loginDays: 0,
        studyHours: 0,
        errorCount: 0,
        studentCount: 0
      },
      
      // 登录记录
      loginRecords: [],
      
      // 偏好设置
      preferences: {
        theme: 'light',
        language: 'zh-CN',
        emailNotification: true,
        desktopNotification: false,
        studyReminder: true
      }
    }
  },
  created() {
    this.loadUserProfile();
    this.loadProfileStats();
    this.loadLoginRecords();
    this.loadPreferences();
  },
  methods: {
    async loadUserProfile() {
      try {
        // 从本地存储获取基本信息
        const localUserInfo = localStorage.getItem('userInfo');
        if (localUserInfo) {
          const basicInfo = JSON.parse(localUserInfo);
          this.userInfo = { ...this.userInfo, ...basicInfo };
        }
        
        // 从服务器获取详细信息
        const response = await axios.get('/user/profile');
        if (response.data.code === 200) {
          this.userInfo = { ...this.userInfo, ...response.data.data };
          this.editForm = { ...this.userInfo };
        }
      } catch (error) {
        console.error('加载用户信息失败:', error);
        // 使用本地存储的基本信息
        const localUserInfo = localStorage.getItem('userInfo');
        if (localUserInfo) {
          const basicInfo = JSON.parse(localUserInfo);
          this.userInfo = { ...this.userInfo, ...basicInfo };
          this.editForm = { ...this.userInfo };
        }
      }
    },
    
    async loadProfileStats() {
      try {
        const response = await axios.get('/user/profile-stats');
        if (response.data.code === 200) {
          this.profileStats = response.data.data;
        }
      } catch (error) {
        console.error('加载统计数据失败:', error);
        // 使用模拟数据
        this.profileStats = {
          loginDays: 15,
          studyHours: 48,
          errorCount: 23,
          studentCount: 35
        };
      }
    },
    
    async loadLoginRecords() {
      try {
        const response = await axios.get('/user/login-records');
        if (response.data.code === 200) {
          this.loginRecords = response.data.data;
        }
      } catch (error) {
        console.error('加载登录记录失败:', error);
        // 使用模拟数据
        this.loginRecords = [
          {
            id: 1,
            loginTime: new Date(),
            location: '北京市',
            device: 'Chrome 浏览器',
            status: 'success'
          },
          {
            id: 2,
            loginTime: new Date(Date.now() - 24 * 60 * 60 * 1000),
            location: '上海市',
            device: 'Safari 浏览器',
            status: 'success'
          }
        ];
      }
    },
    
    async loadPreferences() {
      try {
        const response = await axios.get('/user/preferences');
        if (response.data.code === 200) {
          this.preferences = { ...this.preferences, ...response.data.data };
        }
      } catch (error) {
        console.error('加载偏好设置失败:', error);
      }
    },
    
    getRoleText(role) {
      const roleMap = {
        student: '学生',
        teacher: '教师',
        parent: '家长'
      };
      return roleMap[role] || '';
    },
    
    enableEdit() {
      this.editMode = true;
      this.editForm = { ...this.userInfo };
    },
    
    cancelEdit() {
      this.editMode = false;
      this.editForm = { ...this.userInfo };
      this.$refs.editForm.resetFields();
    },
    
    async saveProfile() {
      try {
        await this.$refs.editForm.validate();
        
        this.saving = true;
        
        const response = await axios.put('/user/profile', this.editForm);
        if (response.data.code === 200) {
          this.userInfo = { ...this.editForm };
          this.editMode = false;
          this.$message.success('保存成功');
          
          // 更新本地存储
          const localUserInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
          localStorage.setItem('userInfo', JSON.stringify({
            ...localUserInfo,
            username: this.userInfo.username
          }));
        } else {
          this.$message.error(response.data.message || '保存失败');
        }
      } catch (error) {
        console.error('保存用户信息失败:', error);
        this.$message.error('保存失败，请重试');
      } finally {
        this.saving = false;
      }
    },
    
    validateConfirmPassword(rule, value, callback) {
      if (value !== this.passwordForm.newPassword) {
        callback(new Error('两次输入的密码不一致'));
      } else {
        callback();
      }
    },
    
    async changePassword() {
      try {
        await this.$refs.passwordForm.validate();
        
        this.changingPassword = true;
        
        const response = await axios.put('/user/change-password', {
          currentPassword: this.passwordForm.currentPassword,
          newPassword: this.passwordForm.newPassword
        });
        
        if (response.data.code === 200) {
          this.$message.success('密码修改成功');
          this.passwordForm = {
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          };
          this.$refs.passwordForm.resetFields();
        } else {
          this.$message.error(response.data.message || '密码修改失败');
        }
      } catch (error) {
        console.error('修改密码失败:', error);
        this.$message.error('密码修改失败，请重试');
      } finally {
        this.changingPassword = false;
      }
    },
    
    async updatePreferences() {
      try {
        const response = await axios.put('/user/preferences', this.preferences);
        if (response.data.code === 200) {
          this.$message.success('设置已保存');
        }
      } catch (error) {
        console.error('保存偏好设置失败:', error);
        this.$message.error('保存失败，请重试');
      }
    },
    
    uploadAvatar() {
      this.avatarDialogVisible = true;
      this.tempAvatar = '';
    },
    
    handleAvatarSuccess(res, file) {
      this.tempAvatar = URL.createObjectURL(file.raw);
    },
    
    beforeAvatarUpload(file) {
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png';
      const isLt2M = file.size / 1024 / 1024 < 2;

      if (!isJPG) {
        this.$message.error('头像图片只能是 JPG/PNG 格式!');
      }
      if (!isLt2M) {
        this.$message.error('头像图片大小不能超过 2MB!');
      }
      return isJPG && isLt2M;
    },
    
    async confirmAvatar() {
      if (this.tempAvatar) {
        this.userInfo.avatar = this.tempAvatar;
        this.avatarDialogVisible = false;
        this.$message.success('头像更新成功');
      }
    },
    
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.getFullYear() + '-' + 
             String(d.getMonth() + 1).padStart(2, '0') + '-' + 
             String(d.getDate()).padStart(2, '0');
    },
    
    formatDateTime(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.getFullYear() + '-' + 
             String(d.getMonth() + 1).padStart(2, '0') + '-' + 
             String(d.getDate()).padStart(2, '0') + ' ' +
             String(d.getHours()).padStart(2, '0') + ':' + 
             String(d.getMinutes()).padStart(2, '0') + ':' + 
             String(d.getSeconds()).padStart(2, '0');
    }
  }
}
</script>

<style lang="less" scoped>
.user-profile {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 60px);
}

// 头部区域
.profile-header {
  background: white;
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.user-avatar-section {
  display: flex;
  align-items: center;
  gap: 24px;
}

.avatar-container {
  position: relative;
  
  img {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    object-fit: cover;
    border: 4px solid #f0f2f5;
  }
  
  .avatar-upload {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 28px;
    height: 28px;
    background: #3498db;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border: 2px solid white;
    
    i {
      color: white;
      font-size: 12px;
    }
    
    &:hover {
      background: #2980b9;
    }
  }
}

.user-basic-info {
  .username {
    margin: 0 0 8px 0;
    font-size: 24px;
    font-weight: 600;
    color: #2c3e50;
  }
  
  .user-role {
    font-size: 16px;
    color: #3498db;
    margin-bottom: 4px;
  }
  
  .join-date {
    font-size: 14px;
    color: #7f8c8d;
  }
}

.profile-stats {
  display: flex;
  gap: 32px;
}

.stat-item {
  text-align: center;
  
  .stat-number {
    font-size: 28px;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 4px;
  }
  
  .stat-label {
    font-size: 14px;
    color: #7f8c8d;
  }
}

// 内容区域
.profile-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.content-tabs {
  display: flex;
  border-bottom: 1px solid #e8e8e8;
}

.tab-item {
  padding: 16px 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #7f8c8d;
  transition: all 0.2s;
  border-bottom: 2px solid transparent;
  
  &:hover {
    color: #3498db;
    background-color: #f8f9fa;
  }
  
  &.active {
    color: #3498db;
    border-bottom-color: #3498db;
    background-color: #f8f9fa;
  }
  
  i {
    font-size: 14px;
  }
}

.tab-content {
  padding: 32px;
}

.tab-panel {
  .info-section, .security-section, .preferences-section {
    margin-bottom: 32px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    h3 {
      margin: 0 0 24px 0;
      font-size: 18px;
      font-weight: 600;
      color: #2c3e50;
      padding-bottom: 12px;
      border-bottom: 1px solid #e8e8e8;
    }
  }
}

// 登录记录
.login-records {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.record-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.record-info {
  .record-time {
    font-weight: 500;
    color: #2c3e50;
    margin-bottom: 4px;
  }
  
  .record-location, .record-device {
    font-size: 14px;
    color: #7f8c8d;
  }
}

.record-status {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  
  &.success {
    background-color: #d4edda;
    color: #155724;
  }
  
  &.failed {
    background-color: #f8d7da;
    color: #721c24;
  }
}

// 偏好设置
.preference-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #f0f2f5;
  
  &:last-child {
    border-bottom: none;
  }
  
  .preference-label {
    font-weight: 500;
    color: #2c3e50;
  }
}

// 头像上传对话框
.avatar-upload-dialog {
  text-align: center;
}

.avatar-uploader {
  .avatar-preview {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    object-fit: cover;
  }
  
  .avatar-uploader-icon {
    font-size: 28px;
    color: #8c939d;
    width: 120px;
    height: 120px;
    line-height: 120px;
    text-align: center;
    border: 1px dashed #d9d9d9;
    border-radius: 50%;
  }
}

.upload-tips {
  margin-top: 16px;
  
  p {
    margin: 0;
    font-size: 14px;
    color: #7f8c8d;
  }
}

// 响应式设计
@media (max-width: 1200px) {
  .header-content {
    flex-direction: column;
    gap: 24px;
    text-align: center;
  }
  
  .profile-stats {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .user-profile {
    padding: 15px;
  }
  
  .profile-header {
    padding: 24px;
  }
  
  .user-avatar-section {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }
  
  .profile-stats {
    gap: 24px;
  }
  
  .content-tabs {
    flex-direction: column;
  }
  
  .tab-content {
    padding: 24px;
  }
  
  .preference-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}
</style>
